import { EXPLOSION_FRAMES } from '../spriteLayout';
import { drawSprite } from './drawSprite';
import type { Blast, Scene } from './types';
import type { PlaybackState, SmokeScreenFx } from './playbackState';

// Blurred, darkened cover copy of the map — fills the letterbox gutters around the
// sharp world layer (drawn inside the stage transform by the engine). iOS Safari
// ignores canvas `ctx.filter = 'blur()'`, so the blur is baked by shrinking the
// image into a tiny canvas once; the upscale at draw time smooths it into a blur.
const blurCache = new WeakMap<HTMLImageElement, HTMLCanvasElement>();
const blurOf = (bg: HTMLImageElement): HTMLCanvasElement | null => {
  const cached = blurCache.get(bg);
  if (cached) return cached;
  if (!bg.complete || !bg.naturalWidth) return null;
  const w = 48;
  const h = Math.max(1, Math.round((w * bg.naturalHeight) / bg.naturalWidth));
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const x = c.getContext('2d');
  if (!x) return null;
  x.imageSmoothingEnabled = true;
  x.drawImage(bg, 0, 0, w, h);
  blurCache.set(bg, c);
  return c;
};

const drawBackground = (ctx: CanvasRenderingContext2D, bg: HTMLImageElement, cw: number, ch: number) => {
  const src = blurOf(bg);
  if (!src) {
    // image not decoded yet — draw it sharp (cover) until the blur copy is ready
    const s = Math.max(cw / bg.width, ch / bg.height);
    ctx.drawImage(bg, (cw - bg.width * s) / 2, (ch - bg.height * s) / 2, bg.width * s, bg.height * s);
    return;
  }
  const smoothed = ctx.imageSmoothingEnabled;
  ctx.imageSmoothingEnabled = true; // upscaling the tiny copy bilinearly = the blur
  const scale = Math.max(cw / src.width, ch / src.height) * 1.06;
  const w = src.width * scale, h = src.height * scale;
  ctx.drawImage(src, (cw - w) / 2, (ch - h) / 2, w, h);
  ctx.imageSmoothingEnabled = smoothed;
  ctx.fillStyle = 'rgba(0,0,0,0.4)'; // darken (the brightness(0.6) the iOS filter dropped)
  ctx.fillRect(0, 0, cw, ch);
};

/** The STATIC backdrop — blurred/darkened gutters + the sharp map — composited ONCE into an
 *  offscreen canvas and reused every frame (blitted by the caller). Rebuilt only when the size,
 *  DPR, letterbox field, blur-readiness or map image changes; otherwise the cached canvas is
 *  returned untouched. Replaces the per-frame blur upscale + darken fill + map draw with a
 *  single drawImage on the hot path. */
export const ensureBackdrop = (
  sc: Scene, cw: number, ch: number, dpr: number, fieldX: number, offY: number, scale: number,
): HTMLCanvasElement | null => {
  const key = `${Math.round(cw)}x${Math.round(ch)}x${dpr}|${fieldX.toFixed(2)},${offY.toFixed(2)},${scale.toFixed(4)}|${blurOf(sc.bg) ? 1 : 0}|${sc.bg.src}`;
  if (sc.backdrop && sc.backdrop.key === key) return sc.backdrop.canvas;
  const dw = Math.max(1, Math.round(cw * dpr));
  const dh = Math.max(1, Math.round(ch * dpr));
  const cv = sc.backdrop?.canvas ?? document.createElement('canvas');
  cv.width = dw; // (re)assigning size also clears the canvas for a fresh composite
  cv.height = dh;
  const octx = cv.getContext('2d');
  if (!octx) return null;
  octx.setTransform(dpr, 0, 0, dpr, 0, 0);
  drawBackground(octx, sc.bg, cw, ch);
  octx.save();
  octx.translate(fieldX, offY);
  octx.scale(scale, scale);
  octx.drawImage(sc.bg, 0, 0, sc.W, sc.H); // the sharp world-space map, baked into the backdrop
  octx.restore();
  sc.backdrop = { canvas: cv, key };
  return cv;
};

export const drawEffects = (sc: Scene, time: number) => {
  const { ctx, bullets, flashes, impacts, grenades, slashes, nade, smokeNade, flashNade, slash } = sc;
  ctx.lineCap = 'round';
  for (const b of bullets) {
    const k = (time - b.t0) / b.life;
    const x = b.fromX + (b.toX - b.fromX) * k;
    const y = b.fromY + (b.toY - b.fromY) * k;
    ctx.strokeStyle = `rgba(255, 224, 118, ${1 - k})`;
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(b.fromX, b.fromY);
    ctx.lineTo(x, y);
    ctx.stroke();
  }
  for (const fl of flashes) {
    const k = (time - fl.t0) / fl.life;
    ctx.save();
    ctx.translate(fl.x, fl.y);
    ctx.rotate(fl.angle);
    ctx.fillStyle = `rgba(255, 238, 120, ${1 - k})`;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(36, -10);
    ctx.lineTo(58, 0);
    ctx.lineTo(36, 10);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }
  for (const im of impacts) {
    const k = (time - im.t0) / im.life;
    ctx.strokeStyle = `rgba(255, 245, 180, ${1 - k})`;
    ctx.lineWidth = 2.5;
    for (let i = 0; i < 6; i++) {
      const a = (Math.PI * 2 * i) / 6;
      ctx.beginPath();
      ctx.moveTo(im.x, im.y);
      ctx.lineTo(im.x + Math.cos(a) * 22 * k, im.y + Math.sin(a) * 22 * k);
      ctx.stroke();
    }
  }
  for (const g of grenades) {
    const k = (time - g.t0) / (g.land - g.t0);
    // forward travel eases out a bit, like a thrown object losing momentum
    const fwd = 1 - (1 - k) * (1 - k);
    const x = g.fromX + (g.toX - g.fromX) * fwd;
    const ground = g.fromY + (g.toY - g.fromY) * fwd;
    // bouncing: a few arches, each lower than the last (amplitude decays to 0 at land)
    const BOUNCES = 3;
    const hop = Math.abs(Math.sin(k * Math.PI * BOUNCES)) * 230 * (1 - k) * (1 - k);
    const squash = hop < 6 ? 1.5 : 1; // flatten the ground shadow at each touchdown
    ctx.save();
    ctx.globalAlpha = 0.3;
    ctx.fillStyle = '#000';
    ctx.beginPath();
    ctx.ellipse(x, ground, 14 * squash, 6 / squash, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
    const spr = g.flash ? flashNade : g.smoke ? smokeNade : nade;
    const gs = 70 / spr.bh;
    ctx.save();
    ctx.translate(x, ground - hop);
    ctx.rotate(k * 20); // spin faster for a livelier tumble
    ctx.scale(gs, gs);
    drawSprite(ctx, spr, -(spr.bx + spr.bw / 2), -(spr.by + spr.bh / 2));
    ctx.restore();
  }
  // knife slash — a sprite swoosh sweeping across the struck target (fallback: arc)
  for (const sl of slashes) {
    const k = (time - sl.t0) / sl.life;
    ctx.save();
    ctx.translate(sl.x, sl.y);
    ctx.rotate(sl.angle - 0.6 + k * 1.2);
    ctx.globalAlpha = 1 - k;
    const ss = 200 / slash.bw; // swoosh drawn ~200px wide
    ctx.scale(ss, ss);
    drawSprite(ctx, slash, -(slash.bx + slash.bw / 2), -(slash.by + slash.bh / 2));
    ctx.restore();
  }
};

// An explosion lives in the y-sorted WORLD pass (same as smoke): it reads in front of
// the fighters it hits and behind anyone standing closer, sorted by its ground anchor.
export const drawOneBlast = (sc: Scene, bl: Blast, time: number) => {
  const { ctx, explosionImg, explosionFrames } = sc;
  const frames = explosionFrames.length
    ? explosionFrames
    : Array.from({ length: EXPLOSION_FRAMES }, (_, i) => ({
        sx: (explosionImg.width / EXPLOSION_FRAMES) * i,
        sw: explosionImg.width / EXPLOSION_FRAMES, sy: 0, sh: explosionImg.height,
      }));
  const k = Math.min(0.999, (time - bl.t0) / bl.life);
  const fr = frames[Math.min(frames.length - 1, (k * frames.length) | 0)];
  const size = bl.r * 2.4;
  const h = (size * fr.sh) / fr.sw;
  ctx.drawImage(explosionImg, fr.sx, fr.sy, fr.sw, fr.sh, bl.x - size / 2, bl.y - h / 2, size, h);
};

const CASING_G = 1600; // gravity on a spent shell, world px/s²
const CASING_BOUNCE = 0.4; // restitution of the little hop off the ground
const CASING_REST_S = 3.6; // how long a shell lies on the ground before it starts to fade
const CASING_FADE_S = 1.3; // fade-out time once its rest is up

/** Eject a spent brass shell from a weapon that just fired. It pops UP + a little to the
 *  shooter's firing side from behind the muzzle, tumbles as it falls, lands on the ground at the
 *  shooter's feet (`feetY`) and lies there piling up until it fades. Purely cosmetic — the
 *  non-deterministic scatter isn't part of the sim. */
export const spawnCasing = (sc: Scene, muzzleX: number, muzzleY: number, angle: number, dir: 1 | -1, feetY: number, mine: boolean, time: number) => {
  const r = Math.random;
  // origin: back down the barrel toward the receiver, a touch above the barrel line
  const x0 = muzzleX - Math.cos(angle) * 26;
  const y0 = muzzleY - Math.sin(angle) * 26 - 8;
  // lively fan-out: a firing-side bias PLUS a big symmetric jitter (some shells kick back the other
  // way), a varied pop height (so they land at different distances) and a bit of ground-depth
  // spread — shells scatter around the fighter instead of stacking in one heap under the weapon
  const vx = dir * (30 + r() * 90) + (r() - 0.5) * 150;
  const vy = -(270 + r() * 240);
  const groundY = feetY + (r() - 0.35) * 46;
  // t when y0 + vy·t + ½G·t² = groundY (positive root — the downward crossing)
  const landT = (-vy + Math.sqrt(vy * vy + 2 * CASING_G * (groundY - y0))) / CASING_G;
  const landX = x0 + vx * landT;
  // little bounce off the ground: reflect vertical velocity (damped), keep some horizontal
  const bounceVy = -CASING_BOUNCE * (vy + CASING_G * landT); // up (vy+G·landT = downward speed)
  const bounceVx = vx * 0.5;
  const landT2 = landT + (-2 * bounceVy) / CASING_G; // up-and-back-down of the hop
  sc.casings.push({
    x0, y0, vx, vy,
    groundY, landT, landX,
    bounceVx, bounceVy, landT2, restX: landX + bounceVx * (landT2 - landT),
    restRot: (r() < 0.5 ? 1 : -1) * (0.06 + r() * 0.16), // near-flat once settled
    rot0: r() * Math.PI * 2,
    spin: (12 + r() * 12) * (r() < 0.5 ? -1 : 1),
    flip: r() < 0.5 ? 1 : -1,
    t0: time, life: landT2 + CASING_REST_S + CASING_FADE_S,
    landed: false, mine,
  });
};

export const drawOneCasing = (sc: Scene, c: Scene['casings'][number], time: number) => {
  const { ctx, casing } = sc;
  const cs = 20 / casing.bw; // ~20 world px wide
  const t = time - c.t0;
  if (t < 0 || t >= c.life) return;
  // 1) first arc + tumble  2) little bounce off the ground  3) rest flat where it settles
  let x: number, y: number, rot: number;
  if (t < c.landT) {
    x = c.x0 + c.vx * t;
    y = c.y0 + c.vy * t + 0.5 * CASING_G * t * t;
    rot = c.rot0 + c.spin * t;
  } else if (t < c.landT2) {
    const tb = t - c.landT;
    x = c.landX + c.bounceVx * tb;
    y = c.groundY + c.bounceVy * tb + 0.5 * CASING_G * tb * tb;
    rot = c.rot0 + c.spin * (c.landT + tb * 0.4); // tumble slows on the hop
  } else {
    x = c.restX; y = c.groundY; rot = c.restRot;
  }
  ctx.save();
  ctx.globalAlpha = t > c.life - CASING_FADE_S ? Math.max(0, (c.life - t) / CASING_FADE_S) : 1;
  ctx.translate(x, y);
  ctx.rotate(rot);
  ctx.scale(c.flip * cs, cs);
  drawSprite(ctx, casing, -(casing.bx + casing.bw / 2), -(casing.by + casing.bh / 2));
  ctx.restore();
};

/** Puff a soft grey wisp from the muzzle on a shot — drifts up + a little forward, then grows
 *  and fades. Cosmetic, spawned alongside the muzzle flash. `oy` = the shooter's ground y,
 *  the painter's-algorithm depth key. */
export const spawnMuzzlePuff = (sc: Scene, x: number, y: number, angle: number, oy: number, time: number) => {
  const r = Math.random;
  // seed it a touch past the muzzle so it reads as coming OUT of the barrel
  sc.muzzlePuffs.push({
    x: x + Math.cos(angle) * 10,
    y: y + Math.sin(angle) * 10,
    vx: Math.cos(angle) * (18 + r() * 16),
    vy: Math.sin(angle) * 18 - (30 + r() * 20), // forward drift + a rising bias
    oy,
    t0: time, life: 0.75 + r() * 0.3,
  });
};

export const drawOneMuzzlePuff = (sc: Scene, p: Scene['muzzlePuffs'][number], time: number) => {
  const { ctx } = sc;
  const t = time - p.t0;
  if (t < 0) return;
  const k = t / p.life;
  if (k >= 1) return;
  const cx = p.x + p.vx * t;
  const cy = p.y + p.vy * t;
  const rad = 9 + k * 24; // grows as it disperses
  const alpha = (1 - k) * 0.6;
  const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, rad);
  g.addColorStop(0, `rgba(226,226,230,${alpha})`);
  g.addColorStop(0.55, `rgba(220,220,226,${alpha * 0.55})`);
  g.addColorStop(1, 'rgba(216,216,224,0)');
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(cx, cy, rad, 0, Math.PI * 2);
  ctx.fill();
};

const SMOKE_FADE_S = 0.45; // seconds the screen takes to drift away once its coverage ends
const SMOKE_BLOOM_S = 0.35; // seconds the cloud takes to pop open once the canister lands
/** One standing smoke screen — the cloud's SINGLE owner from landing to expiry. Invisible
 *  until `bornAt` (the canister landing, stamped by the pump), blooms in over SMOKE_BLOOM_S,
 *  stands swaying through its whole coverage (throw round AND the follow-up round), then
 *  drifts away over SMOKE_FADE_S once `fadeAt` is stamped. One owner = no bloom→standing
 *  handoff. */
export const drawSmokeScreen = (
  sc: Scene, id: string, s: SmokeScreenFx,
  time: number, state: PlaybackState,
) => {
  const { ctx, smokeCloud } = sc;
  if (s.bornAt == null || time < s.bornAt) return; // canister still in the air
  // the smoked fighter died — the screen has nothing to cover, let it drift off from now
  if (sc.byId[id]?.dead && s.fadeAt == null) s.fadeAt = time;
  const bloom = Math.min(1, (time - s.bornAt) / SMOKE_BLOOM_S);
  let alpha = 0.85 * bloom;
  if (s.fadeAt != null) {
    // coverage over (stamped by scheduleRound at the next round's start, or death above):
    // dissipate over SMOKE_FADE_S — never a hard blink-off — then drop the entry.
    const k = (time - s.fadeAt) / SMOKE_FADE_S;
    if (k >= 1) { state.smokeScreens.delete(id); return; }
    alpha *= 1 - Math.max(0, k);
  }
  // FIXED anchor (captured at schedule time) — the cloud stands where it bloomed; it must
  // not chase the body through a knife lunge.
  const sway = Math.sin(time * 1.6 + s.x) * 16;
  const bob = Math.sin(time * 1.05 + s.x) * 6;
  ctx.save();
  ctx.globalAlpha = alpha;
  const cs = (240 * (0.55 + 0.45 * bloom)) / smokeCloud.bw; // pops open 55% → full size
  ctx.translate(s.x + sway, s.y - 90 + bob);
  ctx.scale(cs, cs);
  drawSprite(ctx, smokeCloud, -(smokeCloud.bx + smokeCloud.bw / 2), -(smokeCloud.by + smokeCloud.bh / 2));
  ctx.restore();
};

// flashbang detonation — a bright white core + expanding shock ring over the cluster
export const drawFlashbangs = (sc: Scene, time: number) => {
  const { ctx, flashbangs } = sc;
  for (const fb of flashbangs) {
    const k = (time - fb.t0) / fb.life;
    if (k < 0 || k > 1) continue;
    const a = 1 - k; // fade out over the whole life
    ctx.save();
    // expanding shock ring
    ctx.globalAlpha = a * 0.8;
    ctx.lineWidth = 6 * (1 - k);
    ctx.strokeStyle = '#fffbe0';
    ctx.beginPath();
    ctx.arc(fb.x, fb.y, 30 + k * 220, 0, Math.PI * 2);
    ctx.stroke();
    // hot white core, biggest at the pop
    const core = 90 * (1 - k * 0.7);
    const g = ctx.createRadialGradient(fb.x, fb.y, 0, fb.x, fb.y, core);
    g.addColorStop(0, `rgba(255,255,255,${a})`);
    g.addColorStop(0.6, `rgba(255,250,210,${a * 0.7})`);
    g.addColorStop(1, 'rgba(255,250,210,0)');
    ctx.globalAlpha = 1;
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(fb.x, fb.y, core, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
};

/** Screen-space white-out alpha for a just-popped flashbang (POV blind), peaks ~0.8
 * at the pop and fades out over the first third of the burst. 0 = nothing live. */
export const flashbangWash = (sc: Scene, time: number): number => {
  let wash = 0;
  for (const fb of sc.flashbangs) {
    const k = (time - fb.t0) / fb.life;
    if (k < 0 || k > 0.34) continue;
    wash = Math.max(wash, 0.8 * (1 - k / 0.34));
  }
  return wash;
};

// floating "-N" damage numbers that rise above a fighter's head and fade out
export const drawDamages = (sc: Scene, time: number) => {
  const { ctx, damages } = sc;
  ctx.save();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  for (const d of damages) {
    const k = (time - d.t0) / d.life;
    if (k < 0 || k > 1) continue;
    const alpha = 1 - k * k;
    const size = d.label ? 22 : d.crit ? 38 : 28;
    const y = d.y - 210 - 80 * k; // start above the head, drift up
    ctx.font = `900 ${size}px system-ui, sans-serif`;
    ctx.lineWidth = 5;
    ctx.strokeStyle = `rgba(0,0,0,${0.55 * alpha})`;
    ctx.fillStyle = d.label
      ? `rgba(215,221,230,${alpha})` // серая плашка промаха («МИМО/УВОРОТ/ОСЛЕП»)
      : d.heal
      ? `rgba(57,208,90,${alpha})`
      : d.absorb ? `rgba(247,185,62,${alpha})` // adrenaline — the shield granted (+) / ate (-) it
      : d.shield ? `rgba(90,160,255,${alpha})` // focus braced — hit deflected (reduced)
      : d.crit ? `rgba(255,196,64,${alpha})` : `rgba(255,92,80,${alpha})`;
    const text = d.label ?? (d.heal || d.gain ? `+${d.amount}` : `-${d.amount}`);
    ctx.strokeText(text, d.x, y);
    ctx.fillText(text, d.x, y);
  }
  ctx.restore();
};
